import type { ShippingOption, ShippingQuote } from '@/features/storefront/api/storefront';
import type { ContentReader } from '@/features/storefront/content/useContent';
import { formatDate, getActiveLocale } from '@/lib/i18n/format';

/**
 * Delivery, in words.
 *
 * Shared by the till, the basket and the order page, because all three show
 * the same courier and a shopper who reads "Arrives by 14 Sep" at the till
 * should read the same on the order they placed.
 */

/** An Indian PIN code: six digits, not starting with zero. The checkout schema's rule. */
export const PIN_CODE = /^[1-9][0-9]{5}$/;

const DELIVERY_DATE: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };

/**
 * A courier's date, as a shopper reads it.
 *
 * The estimate arrives as a plain `YYYY-MM-DD`, and `new Date` on that reads
 * it as UTC midnight - which, printed in a zone west of Greenwich, is the day
 * before. Taken apart and rebuilt as a local date so the day is the day.
 */
export function formatDeliveryDate(value: string, locale: string = getActiveLocale()): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const date = match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    : new Date(value);
  return formatDate(date, locale, DELIVERY_DATE);
}

/** "Arrives by 14 Sep 2026", or "3 days", or nothing when the courier said nothing. */
export function describeEta(
  option: ShippingOption,
  text: ContentReader,
  locale: string = getActiveLocale(),
): string | null {
  if (option.estimated_delivery) {
    return text('checkout.delivery_arrives', {
      date: formatDeliveryDate(option.estimated_delivery, locale),
    });
  }
  if (option.estimated_days === null || option.estimated_days <= 0) return null;
  return option.estimated_days === 1
    ? text('checkout.delivery_day')
    : text('checkout.delivery_days', { days: option.estimated_days });
}

const ESTIMATE_DAY: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' };

/**
 * A product page's delivery answer: "Delivered by Thu, 9 Oct" where the shop's
 * delivery settings give a date or a number of days, and otherwise the
 * merchant's own "Usually delivered in 3-7 days" from the component's range.
 */
export function describeDeliveryEstimate(
  quote: ShippingQuote | undefined,
  range: { min: number; max: number },
  text: ContentReader,
  today: Date = new Date(),
  locale: string = getActiveLocale(),
): string {
  const option = quote?.options[0];
  let date: Date | null = null;
  const match = option?.estimated_delivery ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(option.estimated_delivery) : null;
  if (match) date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (!date && option?.estimated_days && option.estimated_days > 0) {
    date = new Date(today.getFullYear(), today.getMonth(), today.getDate() + option.estimated_days);
  }
  if (date) return text('product_page.delivered_by', { date: formatDate(date, locale, ESTIMATE_DAY) });
  return text('product_page.delivered_in', {
    min: Math.min(range.min, range.max),
    max: Math.max(range.min, range.max),
  });
}

/** "Standard · Delhivery", or just "Standard" when the courier is the option. */
export function optionTitle(option: ShippingOption): string {
  return option.courier_name && option.courier_name !== option.name
    ? `${option.name} · ${option.courier_name}`
    : option.name;
}
